import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ArrowLeft, Plus, Loader2, Trash2, GripVertical,
  ChevronDown, ChevronRight, Send, Undo2, Eye, CheckCircle2,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import RichTextEditor from "@/components/RichTextEditor";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonPlanBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleteTopicTarget, setDeleteTopicTarget] = useState(null);
  const [addingTopic, setAddingTopic] = useState(false);
  const [expanded, setExpanded] = useState({}); // topicId -> bool

  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");

  const fetchLesson = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lesson-plans/${id}`, { withCredentials: true });
      setLesson(data);
      setTitleDraft(data.title || "");
      setDescDraft(data.description || "");
      // Auto-expand the first topic if none yet
      if (data.topics?.length) {
        setExpanded((prev) => {
          if (Object.keys(prev).length) return prev;
          return { [data.topics[0].id]: true };
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load lesson plan");
      navigate("/admin/lesson-plans");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchLesson(); }, [fetchLesson]);

  const saveMeta = async () => {
    if (!titleDraft.trim()) { toast.error("Title required"); return; }
    setSavingMeta(true);
    try {
      const { data } = await axios.put(`${API}/lesson-plans/${id}`,
        { title: titleDraft.trim(), description: descDraft },
        { withCredentials: true });
      setLesson((prev) => ({ ...prev, ...data }));
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingMeta(false);
    }
  };

  const togglePublish = async () => {
    setPublishing(true);
    try {
      const newStatus = lesson.status === "published" ? "draft" : "published";
      const { data } = await axios.put(`${API}/lesson-plans/${id}`,
        { status: newStatus },
        { withCredentials: true });
      setLesson((prev) => ({ ...prev, ...data }));
      toast.success(newStatus === "published" ? "Published" : "Reverted to draft");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setPublishing(false);
    }
  };

  const addTopic = async () => {
    setAddingTopic(true);
    try {
      const { data } = await axios.post(`${API}/lesson-plans/${id}/topics`,
        { title: `Topic ${((lesson?.topics?.length) || 0) + 1}`, body: "" },
        { withCredentials: true });
      setLesson((prev) => ({ ...prev, topics: [...(prev.topics || []), { ...data, tasks: [] }] }));
      setExpanded((e) => ({ ...e, [data.id]: true }));
      toast.success("Topic added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add topic failed");
    } finally {
      setAddingTopic(false);
    }
  };

  const deleteTopic = async () => {
    const topic = deleteTopicTarget;
    if (!topic) return;
    try {
      await axios.delete(`${API}/lesson-topics/${topic.id}`, { withCredentials: true });
      setLesson((prev) => ({ ...prev, topics: (prev.topics || []).filter((t) => t.id !== topic.id) }));
      toast.success("Topic deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      throw err;
    }
  };

  const updateTopic = async (topicId, patch) => {
    setLesson((prev) => ({
      ...prev,
      topics: (prev.topics || []).map((t) => (t.id === topicId ? { ...t, ...patch } : t)),
    }));
    try {
      await axios.put(`${API}/lesson-topics/${topicId}`, patch, { withCredentials: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const addTask = async (topicId) => {
    try {
      const { data } = await axios.post(`${API}/lesson-topics/${topicId}/tasks`,
        { content: "" },
        { withCredentials: true });
      setLesson((prev) => ({
        ...prev,
        topics: (prev.topics || []).map((t) => t.id === topicId ? { ...t, tasks: [...(t.tasks || []), data] } : t),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add task failed");
    }
  };

  const updateTask = async (topicId, taskId, content) => {
    setLesson((prev) => ({
      ...prev,
      topics: (prev.topics || []).map((t) =>
        t.id === topicId
          ? { ...t, tasks: (t.tasks || []).map((tk) => tk.id === taskId ? { ...tk, content } : tk) }
          : t),
    }));
    try {
      await axios.put(`${API}/lesson-tasks/${taskId}`, { content }, { withCredentials: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const deleteTask = async (topicId, taskId) => {
    try {
      await axios.delete(`${API}/lesson-tasks/${taskId}`, { withCredentials: true });
      setLesson((prev) => ({
        ...prev,
        topics: (prev.topics || []).map((t) =>
          t.id === topicId ? { ...t, tasks: (t.tasks || []).filter((tk) => tk.id !== taskId) } : t),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === "TOPIC") {
      const topics = [...(lesson.topics || [])];
      const [moved] = topics.splice(source.index, 1);
      topics.splice(destination.index, 0, moved);
      setLesson((prev) => ({ ...prev, topics }));
      try {
        await axios.post(`${API}/lesson-plans/${id}/topics/reorder`,
          { topic_ids: topics.map((t) => t.id) },
          { withCredentials: true });
      } catch {
        toast.error("Reorder failed");
      }
    } else if (type === "TASK") {
      // droppableId format: "tasks-<topicId>"
      const topicId = source.droppableId.replace(/^tasks-/, "");
      if (topicId !== destination.droppableId.replace(/^tasks-/, "")) return; // tasks cannot move between topics (by design)
      const topic = (lesson.topics || []).find((t) => t.id === topicId);
      if (!topic) return;
      const tasks = [...(topic.tasks || [])];
      const [moved] = tasks.splice(source.index, 1);
      tasks.splice(destination.index, 0, moved);
      setLesson((prev) => ({
        ...prev,
        topics: (prev.topics || []).map((t) => t.id === topicId ? { ...t, tasks } : t),
      }));
      try {
        await axios.post(`${API}/lesson-topics/${topicId}/tasks/reorder`,
          { task_ids: tasks.map((x) => x.id) },
          { withCredentials: true });
      } catch {
        toast.error("Reorder failed");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }
  if (!lesson) return null;

  const published = lesson.status === "published";

  return (
    <div className="space-y-6" data-testid="lesson-plan-builder">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/lesson-plans")} className="h-8 px-2 text-[#A1A9B8] hover:text-white hover:bg-white/5" data-testid="lesson-plan-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> All Lesson Plans
          </Button>
        </div>
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-5 space-y-3" data-testid="lesson-plan-header-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] space-y-2">
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                data-testid="lesson-plan-edit-title"
                className="bg-transparent border-0 text-white text-xl sm:text-2xl font-heading font-bold px-0 h-10 focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder="Untitled Lesson Plan"
              />
              <Textarea
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
                data-testid="lesson-plan-edit-description"
                placeholder="Short description for the instructor…"
                className="bg-[#0A0C10] border-white/10 text-white text-sm font-body min-h-[56px]"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                className={`text-[10px] ${published
                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                  : "bg-amber-500/15 text-amber-400 border-amber-500/20"}`}
                data-testid="lesson-plan-status-badge"
              >
                {published ? "Published" : "Draft"}
              </Badge>
              {published && (
                <a
                  href={`/lesson-plans/${id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-xs font-body text-cyan-300 hover:text-cyan-200"
                  data-testid="lesson-plan-public-link"
                >
                  <Eye className="h-3.5 w-3.5 mr-1" /> View
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={saveMeta}
              disabled={savingMeta}
              data-testid="lesson-plan-save-meta"
              className="h-9 px-4 text-sm font-body text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
            >
              {savingMeta ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              {published ? "Update live" : "Save draft"}
            </Button>
            <Button
              onClick={togglePublish}
              disabled={publishing}
              variant="outline"
              data-testid="lesson-plan-toggle-publish"
              className={`h-9 px-4 text-sm font-body border-white/10 ${published ? "text-amber-300 hover:text-amber-200 hover:bg-amber-500/10" : "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"} bg-[#0A0C10]`}
            >
              {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                : published ? <Undo2 className="h-4 w-4 mr-1.5" />
                : <Send className="h-4 w-4 mr-1.5" />}
              {published ? "Unpublish" : "Publish"}
            </Button>
          </div>
        </div>
      </div>

      {/* Topics */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="topics" type="TOPIC">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3" data-testid="topics-list">
              {(lesson.topics || []).map((topic, index) => (
                <Draggable key={topic.id} draggableId={topic.id} index={index}>
                  {(p) => (
                    <div
                      ref={p.innerRef}
                      {...p.draggableProps}
                      className="bg-[#11141A] border border-white/10 rounded-xl"
                      data-testid={`topic-card-${topic.id}`}
                    >
                      <TopicCard
                        topic={topic}
                        expanded={!!expanded[topic.id]}
                        onToggle={() => setExpanded((e) => ({ ...e, [topic.id]: !e[topic.id] }))}
                        dragHandleProps={p.dragHandleProps}
                        onUpdate={(patch) => updateTopic(topic.id, patch)}
                        onDelete={() => setDeleteTopicTarget(topic)}
                        onAddTask={() => addTask(topic.id)}
                        onUpdateTask={(taskId, content) => updateTask(topic.id, taskId, content)}
                        onDeleteTask={(taskId) => deleteTask(topic.id, taskId)}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        onClick={addTopic}
        disabled={addingTopic}
        data-testid="add-topic-btn"
        className="h-10 w-full border-2 border-dashed border-white/10 bg-transparent text-[#A1A9B8] hover:text-white hover:border-white/20 hover:bg-white/[0.02] font-body"
      >
        {addingTopic ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
        Add Topic
      </Button>

      <DeleteConfirmDialog
        open={!!deleteTopicTarget}
        onOpenChange={(open) => { if (!open) setDeleteTopicTarget(null); }}
        tier={1}
        entityName={deleteTopicTarget?.title || "this topic"}
        description="Delete this topic and all its tasks?"
        onConfirm={deleteTopic}
      />
    </div>
  );
}

function TopicCard({ topic, expanded, onToggle, dragHandleProps, onUpdate, onDelete, onAddTask, onUpdateTask, onDeleteTask }) {
  const [titleDraft, setTitleDraft] = useState(topic.title || "");
  const titleDebounce = useRef(null);

  useEffect(() => { setTitleDraft(topic.title || ""); }, [topic.id, topic.title]);

  const onTitleChange = (v) => {
    setTitleDraft(v);
    if (titleDebounce.current) clearTimeout(titleDebounce.current);
    titleDebounce.current = setTimeout(() => {
      onUpdate({ title: v });
    }, 500);
  };

  const bodyDebounce = useRef(null);
  const onBodyChange = (html) => {
    if (bodyDebounce.current) clearTimeout(bodyDebounce.current);
    bodyDebounce.current = setTimeout(() => {
      onUpdate({ body: html });
    }, 800);
  };

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-3">
        <span
          {...dragHandleProps}
          className="cursor-grab text-[#6E7787] hover:text-white active:cursor-grabbing shrink-0"
          data-testid={`topic-drag-${topic.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="text-[#6E7787] hover:text-white shrink-0"
          data-testid={`topic-expand-${topic.id}`}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <Input
          value={titleDraft}
          onChange={(e) => onTitleChange(e.target.value)}
          data-testid={`topic-title-${topic.id}`}
          className="bg-transparent border-0 text-white font-body font-semibold text-base px-0 h-8 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
          placeholder="Topic title"
        />
        <div className="text-xs text-[#6E7787] font-body hidden sm:block">
          {(topic.tasks?.length || 0)} task{(topic.tasks?.length || 0) === 1 ? "" : "s"}
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={onDelete}
          className="h-7 w-7 p-0 text-[#6E7787] hover:text-red-400 hover:bg-red-500/10"
          data-testid={`topic-delete-${topic.id}`}
          title="Delete topic"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {expanded && (
        <div className="border-t border-white/5 px-4 py-4 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-body text-[#6E7787] mb-1.5">Body</div>
            <RichTextEditor
              value={topic.body || ""}
              onChange={onBodyChange}
              placeholder="Add lesson content — instructions, examples, callouts, embedded videos…"
              testId={`topic-body-${topic.id}`}
            />
          </div>

          <TasksList
            topicId={topic.id}
            tasks={topic.tasks || []}
            onAdd={onAddTask}
            onUpdate={onUpdateTask}
            onDelete={onDeleteTask}
          />
        </div>
      )}
    </>
  );
}

function TasksList({ topicId, tasks, onAdd, onUpdate, onDelete }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-body text-[#6E7787] mb-1.5">Tasks</div>
      <Droppable droppableId={`tasks-${topicId}`} type="TASK">
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="space-y-2 mb-2"
            data-testid={`tasks-list-${topicId}`}
          >
            {tasks.length === 0 && (
              <div className="text-xs text-[#6E7787] font-body italic py-1">No tasks yet.</div>
            )}
            {tasks.map((task, idx) => (
              <Draggable key={task.id} draggableId={task.id} index={idx}>
                {(p) => (
                  <div
                    ref={p.innerRef}
                    {...p.draggableProps}
                    className="flex items-center gap-2 bg-[#0A0C10] border border-white/10 rounded-md px-2 py-1.5"
                    data-testid={`task-row-${task.id}`}
                  >
                    <span
                      {...p.dragHandleProps}
                      className="cursor-grab text-[#6E7787] hover:text-white active:cursor-grabbing"
                      data-testid={`task-drag-${task.id}`}
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-xs text-[#6E7787] font-body w-6 text-center shrink-0">{idx + 1}.</span>
                    <TaskContentInput
                      value={task.content}
                      onCommit={(v) => onUpdate(task.id, v)}
                      testId={`task-input-${task.id}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => onDelete(task.id)} data-testid={`task-delete-${task.id}`} className="h-7 w-7 p-0 text-[#6E7787] hover:text-red-400 hover:bg-red-500/10" title="Delete task">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      <Button
        onClick={onAdd}
        variant="ghost"
        size="sm"
        data-testid={`add-task-${topicId}`}
        className="h-8 text-xs font-body text-[#A1A9B8] hover:text-white hover:bg-white/5"
      >
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Task
      </Button>
    </div>
  );
}

function TaskContentInput({ value, onCommit, testId }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const debounce = useRef(null);
  const handleChange = (v) => {
    setDraft(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => onCommit(v), 500);
  };
  return (
    <Input
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={() => onCommit(draft)}
      placeholder="Describe this step…"
      data-testid={testId}
      className="bg-transparent border-0 text-white text-sm font-body px-1 h-7 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
    />
  );
}
